import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  adminPaymentService,
  PaymentListFilters,
} from "@/services/admin-payment.service";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { showPaymentError } from "@/lib/errorUtils";
import { formatCurrency } from "@/lib/currency";
import {
  hasPaymentProof,
  PaymentProofRequiredError,
} from "@/lib/paymentProof";
import { usePermissions } from "@/hooks/usePermissions";
import { awaitRefresh } from "@/lib/awaitRefresh";

/** Rows per page of the verification queue. */
const VERIFICATION_QUEUE_PAGE_SIZE = 50;

export function usePaymentQueries() {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = usePermissions();

  /**
   * Verifies a payment with optional notes.
   * On success, creates account balance transaction and invalidates payment queries.
   */
  const verifyPayment = useMutation({
    mutationFn: async ({
      paymentId,
      notes,
      receivedAmount,
    }: {
      paymentId: string;
      notes?: string;
      receivedAmount: number;
    }) => {
      // Single gate for every verify entry point (list row, detail page,
      // verification queue): a payment with no proof attached may only be
      // verified by a SuperAdmin. Mirrors VerifyPaymentCommandHandler.
      if (!isSuperAdmin()) {
        // Same key/staleTime as usePaymentDetail, so an already-open payment
        // detail is reused instead of refetched.
        const detail = await queryClient.fetchQuery({
          queryKey: ["payments", paymentId],
          queryFn: () => adminPaymentService.getPaymentDetails(paymentId),
          staleTime: 10000,
        });

        if (!hasPaymentProof(detail)) {
          throw new PaymentProofRequiredError();
        }
      }

      return adminPaymentService.verifyPayment(paymentId, { notes, receivedAmount });
    },
    onSuccess: (data) => {
      // A null transactionId means the verification moved no money: an order-linked payment is
      // settled by OrderPaidEvent and credits nothing, so `newBalance` is simply the balance as
      // it already stood. Claiming a credit there — and printing a figure next to the claim —
      // told the operator a top-up had happened and invited them to go looking for it.
      toast.success("Payment Verified Successfully", {
        description: data.transactionId
          ? `The payment has been verified and the tenant's balance has been credited.\n\nNew balance: ${formatCurrency(data.newBalance, "VND")}`
          : "The payment has been verified. It settles an order, so no balance was credited.",
      });
    },
    onError: (error: AxiosError | Error, variables) => {
      // Shared with `PaymentVerificationPage`'s "left the queue" effect: when a failed verify's
      // refetch drops the payment from the list, that effect reports it under the SAME id, so
      // sonner replaces this toast with that sentence instead of stacking a second one on top of
      // it. When the payment is still in the list, that effect never fires and this toast is what
      // the operator is left with — hence it has to stand on its own too.
      const toastId = `verify-${variables.paymentId}`;

      if (error instanceof PaymentProofRequiredError) {
        toast.error("Cannot verify payment", {
          description: error.message,
          id: toastId,
        });
        return;
      }

      showPaymentError(error, {
        error: (title: string, options?: Record<string, unknown>) =>
          toast.error(title, { ...options, id: toastId }),
        warning: (title: string, options?: Record<string, unknown>) =>
          toast.warning(title, { ...options, id: toastId }),
        info: (title: string, options?: Record<string, unknown>) =>
          toast.info(title, { ...options, id: toastId }),
      });
    },
    // Returned so `isPending` stays true until the refreshed rows are on screen — the dialog
    // (BusyOverlay) and the queue row both key their pending state off this mutation. Runs on
    // the error path too, which is what reloads the queue after a refusal caused by the payment
    // having moved (CONFLICT, ORDER_ALREADY_SETTLED): the toast says the list was refreshed, and
    // this is the refresh. Capped by `awaitRefresh`: the write already landed, so a slow re-read
    // must not hold the overlay for longer than a few seconds.
    onSettled: () =>
      awaitRefresh(queryClient.invalidateQueries({ queryKey: ["payments"] })),
  });

  /**
   * Rejects a payment with required reason.
   * Marks payment as rejected and notifies tenant.
   */
  const rejectPayment = useMutation({
    mutationFn: ({
      paymentId,
      reason,
    }: {
      paymentId: string;
      reason: string;
    }) => adminPaymentService.rejectPayment(paymentId, { reason }),
    onSuccess: () => {
      toast.success("Payment Rejected", {
        description:
          "The payment has been rejected successfully. The tenant will be notified.",
      });
    },
    onError: (error: AxiosError | Error, variables) => {
      // Same id-sharing trick as verifyPayment's onError above, so
      // `PaymentVerificationPage`'s "left the queue" effect replaces this toast rather than
      // stacking a second one when it fires.
      const toastId = `reject-${variables.paymentId}`;

      showPaymentError(error, {
        error: (title: string, options?: Record<string, unknown>) =>
          toast.error(title, { ...options, id: toastId }),
        warning: (title: string, options?: Record<string, unknown>) =>
          toast.warning(title, { ...options, id: toastId }),
        info: (title: string, options?: Record<string, unknown>) =>
          toast.info(title, { ...options, id: toastId }),
      });
    },
    // Reject cancels the order behind the payment (see RejectPaymentCommandHandler), so the
    // admin orders views are stale too — not just the payments one. The keys are the ones
    // useAdminOrders actually uses; a bare ["orders"] matched nothing in this app.
    onSettled: () =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["payments"] }),
          queryClient.invalidateQueries({ queryKey: ["admin-orders"] }),
          queryClient.invalidateQueries({ queryKey: ["admin-order-statistics"] }),
        ]),
      ),
  });

  /**
   * Cancels a selection of unsettled payments in one request.
   *
   * Deliberately does NOT toast on its own: the response is a per-payment report, and a blanket
   * "done" would hide the rows that were skipped because they settled while the operator was
   * choosing. The caller renders the report and decides what to say.
   */
  const bulkCancelPayments = useMutation({
    mutationFn: ({
      paymentIds,
      reason,
    }: {
      paymentIds: string[];
      reason: string;
    }) => adminPaymentService.bulkCancelPayments({ paymentIds, reason }),
    onError: (error: AxiosError | Error) => {
      showPaymentError(error, toast);
    },
    // See verifyPayment's onSettled above — same reasoning, including the `awaitRefresh` cap;
    // this mutation has no success toast of its own (the caller renders the per-payment
    // report), so onSettled is all it needs.
    onSettled: () =>
      awaitRefresh(queryClient.invalidateQueries({ queryKey: ["payments"] })),
  });

  /**
   * Writes or clears the operator's internal note on a payment.
   *
   * Invalidates `["payments"]` like every other write here, so the list cell, the detail card and
   * the note's author line all come back from the server rather than being patched in place — the
   * author and timestamp are stamped server-side and the client has no honest value for them.
   */
  const updatePaymentNote = useMutation({
    mutationFn: ({
      paymentId,
      note,
    }: {
      paymentId: string;
      note: string | null;
    }) => adminPaymentService.updatePaymentNote(paymentId, note),
    onSuccess: (_data, variables) => {
      toast.success(variables.note ? "Note saved" : "Note removed");
    },
    onError: (error: AxiosError | Error) => {
      showPaymentError(error, toast);
    },
    onSettled: () =>
      awaitRefresh(queryClient.invalidateQueries({ queryKey: ["payments"] })),
  });

  return {
    verifyPayment,
    rejectPayment,
    bulkCancelPayments,
    updatePaymentNote,
  };
}

/**
 * The payment verification queue, as one infinite query.
 *
 * Every loaded page lives under the one key, so a refresh — the `["payments"]` invalidation every
 * verify and reject runs — re-reads all of them in order, and the next page is always derived from
 * the last one that loaded. The page-by-page queries this replaced were stitched together by hand:
 * a failed page was skipped on retry, and a first-page refresh wiped the pages appended after it.
 */
export function useVerificationQueue() {
  return useInfiniteQuery({
    queryKey: ["payments", "verification-queue"],
    queryFn: ({ pageParam }) =>
      adminPaymentService.getVerificationQueue(pageParam, VERIFICATION_QUEUE_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.currentPage < lastPage.pagination.totalPages
        ? lastPage.pagination.currentPage + 1
        : undefined,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch payment details by ID.
 * Used for payment detail views and verification workflows.
 *
 * @param paymentId - The payment ID to fetch
 * @returns Query object with payment details
 */
export function usePaymentDetail(paymentId: string) {
  return useQuery({
    queryKey: ["payments", paymentId],
    queryFn: () => adminPaymentService.getPaymentDetails(paymentId),
    enabled: !!paymentId,
    staleTime: 10000, // Consider data stale after 10 seconds
  });
}

/**
 * Hook to fetch all payments with filters for admin management.
 *
 * @param filters - Filter parameters including pagination
 * @returns Query object with payments list and pagination
 */
export function usePaymentsList(filters: PaymentListFilters = {}) {
  return useQuery({
    queryKey: ["payments", "list", filters],
    queryFn: ({ signal }) => adminPaymentService.getPayments(filters, signal),
    // No placeholderData on purpose: keeping the previous page's rows while a new filter loads
    // reads as "the filter changed nothing". staleTime still spares a refetch on remount.
    staleTime: 30000,
  });
}
